/**
 * @file Renders the drawer form that updates an existing entity item.
 */
import { useState } from "react";
import type { ComponentProps, ReactNode } from "react";
import type { FormInstance } from "antd";

import { EntityForm } from "@components/Form";
import type { AppContext } from "@components/AppContext/types";
import type { Operation } from "@api";

import Drawer from "./Drawer";
import DeleteButton from "./DeleteButton";
import type { DrawerRef } from "./types";
import type { EntityItem, TableRef } from "../Modal/types";

type EntityFormPassthroughProps = {
  entity: string;
  operation: Operation;
  schema?: unknown;
  request?: AppContext["request"];
  submitLabel?: string;
  onSubmit?: (parameters: unknown) => unknown;
  submitButtonProps?: Record<string, unknown>;
  successMessage?: string | boolean;
  failedMessage?: string | boolean;
  shouldOptimizeUpdateMutation?: boolean;
  expectedErrors?: string[];
  formProps?: Record<string, unknown>;
  hasSubmitButton?: boolean;
  onFailure?: () => void;
  onSubmitFailed?: () => void;
  onSubmitConfirm?: (parameters: unknown, action: () => unknown) => unknown;
  submitItemProps?: Record<string, unknown>;
};

type Props = {
  onOpen?: (item: EntityItem) => void;
  onSuccess?: (updatedItem: unknown) => unknown;
  form?: FormInstance;
  extra?: ReactNode;
  table?: TableRef;
  width?: number | string;
  onDelete?: () => Promise<unknown>;
  children?: ReactNode;
  deleteButtonProps?: Omit<ComponentProps<typeof DeleteButton>, "onDelete">;
  title: string;
  drawer: DrawerRef;
} & EntityFormPassthroughProps;

/**
 * Renders a drawer with an entity form that updates the open item and
 * optionally offers to delete it.
 */
const UpdateDrawer = ({
  onOpen: onOpenCustom = () => undefined,
  onSuccess: onSuccessCustom = () => undefined,
  form = undefined,
  extra = undefined,
  table = undefined,
  width = undefined,
  onDelete = undefined,
  children = undefined,
  deleteButtonProps = {},
  title,
  drawer,
  ...otherEntityFormProps
}: Props): JSX.Element => {
  const [ item, setItem ] = useState<EntityItem>();

  /**
   * Clears the open item when the drawer closes.
   */
  const onClose = async () =>
    setItem(undefined);

  /**
   * Stores the item the drawer opened with and runs the custom open
   * handler.
   */
  const onOpen = (targetItem?: unknown) => {
    const nextItem = targetItem as EntityItem;
    setItem(nextItem);
    onOpenCustom(nextItem);
  };

  /**
   * Runs the custom success handler, updates the item in the table
   * and closes the drawer.
   */
  const onSuccess = async (updatedItem: unknown) => {
    if (onSuccessCustom) {
      await onSuccessCustom({ ...item, ...updatedItem as object });
    }

    if (table) {
      table.current.updateItem(item.id, updatedItem);
    }

    drawer.current.close();
  };

  const drawerProps = {
    forceRender: true,
    destroyOnClose: true,
    extra,
    title,
    width,
    drawer,
    onOpen,
    onClose,
  };

  const formProps = {
    form,
    item,
    setItem,
    onSuccess,
    ...otherEntityFormProps,
  };

  return (
    <Drawer {...drawerProps}>
      {
        item && (
          <EntityForm {...formProps} />
        )
      }

      {
        children && (
          <>
            {children}
          </>
        )
      }

      {
        onDelete && (
          <DeleteButton
            onDelete={onDelete}
            {...deleteButtonProps}
          />
        )
      }
    </Drawer>
  );
};

export default UpdateDrawer;
